import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import _ from 'lodash';

// Define the Contact interface based on your FormContact model
export interface Contact {
  id: number;
  organization_id: number;
  project_id: number;
  conversation_id?: number;
  type: string;
  value: string;
  entity_name?: string;
  is_owner: boolean;
  created_at: string;
  updated_at: string;
  // Add any other properties from your FormContactResource
}

// Define filter parameters interface
export interface ContactFilterParams {
  type?: string;
  is_owner?: boolean;
  entity_name?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
  sort_by?: 'created_at' | 'type' | 'value' | 'is_owner';
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

// Define pagination interface
export interface Pagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

// Define the contacts state interface
interface ContactsState {
  projectContacts: Contact[];
  organizationContacts: Contact[];
  conversationContacts: Contact[];
  pagination: Pagination | null;
  isLoading: boolean;
  isExporting: boolean;
  error: string | null;
  currentFilter: ContactFilterParams;
}

// Initial state
const initialState: ContactsState = {
  projectContacts: [],
  organizationContacts: [],
  conversationContacts: [],
  pagination: null,
  isLoading: false,
  isExporting: false,
  error: null,
  currentFilter: {
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 15,
    page: 1
  }
};

// Add this utility function at the top of the file after imports
const isEqual = (arr1: Contact[], arr2: Contact[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((contact, index) => 
    JSON.stringify(contact) === JSON.stringify(arr2[index])
  );
};

export const fetchOrganizationContacts = createAsyncThunk(
  'contacts/fetchOrganizationContacts',
  async (
    { organizationId, filters }: { organizationId: number; filters?: ContactFilterParams },
    { rejectWithValue }
  ) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await axiosInstance.get(
        `/organizations/${organizationId}/contacts?${params.toString()}`
      );
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organization contacts');
    }
  }
);

// Async thunk for fetching project contacts
export const fetchProjectContacts = createAsyncThunk(
  'contacts/fetchProjectContacts',
  async (
    { projectIdOrSlug, filters }: { projectIdOrSlug: number | string; filters?: ContactFilterParams },
    { rejectWithValue }
  ) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/contacts?${params.toString()}`
      );
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project contacts');
    }
  }
);

// Async thunk for fetching conversation contacts
export const fetchConversationContacts = createAsyncThunk(
  'contacts/fetchConversationContacts',
  async (
    { 
      conversationId, 
      projectIdOrSlug, 
      filters 
    }: { 
      conversationId: number | string; 
      projectIdOrSlug: number | string; 
      filters?: ContactFilterParams 
    },
    { rejectWithValue }
  ) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }
      
       console.log({projectIdOrSlug, conversationId, params})
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/conversations/${conversationId}/contacts?${params.toString()}`
      );
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversation contacts');
    }
  }
);

// Add new async thunk for exporting PDF
export const exportOrganizationContactsPdf = createAsyncThunk(
  'contacts/exportOrganizationContactsPdf',
  async (
    { organizationId, filters }: { organizationId: number; filters?: ContactFilterParams },
    { rejectWithValue }
  ) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      // Make the request with responseType blob to handle PDF
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/contacts/export/pdf?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `organization_contacts_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export organization contacts as PDF');
    }
  }
);

// Add new async thunk for exporting project contacts as PDF
export const exportProjectContactsPdf = createAsyncThunk(
  'contacts/exportProjectContactsPdf',
  async (
    { projectIdOrSlug, filters }: { projectIdOrSlug: number | string; filters?: ContactFilterParams },
    { rejectWithValue }
  ) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      // Make the request with responseType blob to handle PDF
      const response = await axiosInstance.get(
        `/projects/${projectIdOrSlug}/contacts/export/pdf?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_contacts_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export project contacts as PDF');
    }
  }
);

// Create the contacts slice
const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    // Allows updating filter parameters without fetching data
    setContactFilters: (state, action) => {
      state.currentFilter = {
        ...state.currentFilter,
        ...action.payload
      };
    },
    // Reset filters to default
    resetContactFilters: (state) => {
      state.currentFilter = {
        sort_by: 'created_at',
        sort_order: 'desc',
        per_page: 15,
        page: 1
      };
    },
    // Clear the contacts list (useful when navigating away)
    clearContacts: (state) => {
      state.projectContacts = [];
      state.organizationContacts = [];
      state.conversationContacts = [];
      state.pagination = null;
    }
  },
  extraReducers: (builder) => {
    // Organization contacts handlers
    builder
      .addCase(fetchOrganizationContacts.pending, (state) => {
        if(_.isEmpty(state.organizationContacts)) {
        state.isLoading = true;
        state.error = null;
        }
      })
      .addCase(fetchOrganizationContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Only update if data is different or state was empty
        if (!state.organizationContacts.length || 
            !isEqual(state.organizationContacts, action.payload.contacts) ||
            JSON.stringify(state.pagination) !== JSON.stringify(action.payload.pagination)) {
          state.organizationContacts = action.payload.contacts;
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchOrganizationContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'An error occurred while fetching organization contacts';
      })
      
    // Project contacts handlers
      .addCase(fetchProjectContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Only update if data is different or state was empty
        if (!state.projectContacts.length || 
            !isEqual(state.projectContacts, action.payload.contacts) ||
            JSON.stringify(state.pagination) !== JSON.stringify(action.payload.pagination)) {
          state.projectContacts = action.payload.contacts;
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchProjectContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'An error occurred while fetching project contacts';
      })
      
    // Conversation contacts handlers
      .addCase(fetchConversationContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversationContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Only update if data is different or state was empty
        if (!state.conversationContacts.length || 
            !isEqual(state.conversationContacts, action.payload.contacts) ||
            JSON.stringify(state.pagination) !== JSON.stringify(action.payload.pagination)) {
          state.conversationContacts = action.payload.contacts;
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchConversationContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'An error occurred while fetching conversation contacts';
      })
      
    // Export PDF handlers
    builder
      .addCase(exportOrganizationContactsPdf.pending, (state) => {
        state.isExporting = true;
        state.error = null;
      })
      .addCase(exportOrganizationContactsPdf.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportOrganizationContactsPdf.rejected, (state, action) => {
        state.isExporting = false;
        state.error = action.payload as string || 'An error occurred while exporting contacts to PDF';
      })
      .addCase(exportProjectContactsPdf.pending, (state) => {
        state.isExporting = true;
        state.error = null;
      })
      .addCase(exportProjectContactsPdf.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportProjectContactsPdf.rejected, (state, action) => {
        state.isExporting = false;
        state.error = action.payload as string || 'An error occurred while exporting project contacts to PDF';
      });
  }
});

// Export actions
export const { setContactFilters, resetContactFilters, clearContacts } = contactsSlice.actions;

// Export reducer
export default contactsSlice.reducer; 